import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as S from './ReceiveItemCount.styles'
import { ReceiveItemPageModes } from 'src/features/transfers/pages/TransferReceive/ReceiveItem/ReceiveItem'
import { useSelector } from 'src/utils/store'
import { useState } from 'react'
import TextLabel from '../TextLabel/TextLabel'
import { InputAdornment, Typography } from '@mui/material'
import { actions } from '../../../TransfersState'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import FormCountBatches from '../FormCountBatches/FormCountBatches'
import { ReceiveItem } from 'src/features/transfers/TransfersTypes'
import { toast } from 'react-toastify'

enum ItemCountModes {
  OPERATOR_COUNT = 1,
  SUPERVISOR_BADGE = 2,
  SUPERVISOR_COUNT = 3,
  SUPERVISOR_BATCHES_COUNT = 4,
}

interface ReceiveItemCountProps {
  idCage: number
  item: ReceiveItem
  items: ReceiveItem[]
  setPageMode: React.Dispatch<React.SetStateAction<ReceiveItemPageModes>>
}

interface FormData {
  operatorCount: number
  supervisorBadge: string
  supervisorCount: number
}

const ReceiveItemCount = ({
  idCage,
  item,
  items,
  setPageMode,
}: ReceiveItemCountProps) => {
  const {
    register,
    setError,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>()

  const { receiveSupervisor } = useSelector((state) => state.transfers)

  const dispatch = useDispatch()

  const [itemCountMode, setItemCountMode] = useState(ItemCountModes.OPERATOR_COUNT)

  const totalItemQuantity = items.filter((x) => x.idModel === item.idModel && x.done !== true).flatMap((x) => x.batches).reduce((acc, batch) => acc + batch.quantity, 0)

  const [showModalDivergentQuantityConfirmation, setShowModalDivergentQuantityConfirmation] = useState(false)
  const [divergentQuantity, setDivergentQuantity] = useState<Number>(0)

  const [preventDoubleClick, setPreventDoubleClick] = useState(false)

  const nextItemCountMode = () => {
    setItemCountMode(itemCountMode + 1)
  }

  const onCloseModalDivergentQuantityConfirmation = () => {
    if (preventDoubleClick) {
      return
    }

    setShowModalDivergentQuantityConfirmation(false)
  }

  const divergentQuantityConfirmation = () => {
    if (preventDoubleClick) {
      return
    }

    if (itemCountMode === ItemCountModes.SUPERVISOR_COUNT && divergentQuantity === 0) {
      setPreventDoubleClick(true)

      const onSuccess = () => {
        setShowModalDivergentQuantityConfirmation(false)
        setPreventDoubleClick(false)
        setDivergentQuantity(0)
        dispatch(actions.setProperty({ property: 'receiveSupervisor', value: undefined }))
        toast.info('Produto registrado como não encontrado')
      }

      const onError = () => {
        setPreventDoubleClick(false)
      }

      dispatch(actions.receiveSupervisorProductNotFound({
        badge: receiveSupervisor?.badge || '',
        idCage,
        item,
        onSuccess,
        onError,
      }))
    } else {
      setShowModalDivergentQuantityConfirmation(false)
      setDivergentQuantity(0)
      nextItemCountMode()
    }
  }

  const onOperatorCount = ({ operatorCount }: FormData) => {
    const quantity = Number(operatorCount)

    if (quantity !== totalItemQuantity) {
      setDivergentQuantity(quantity)
      setShowModalDivergentQuantityConfirmation(true)
    } else {
      setPageMode(ReceiveItemPageModes.ITEM_MOVEMENT)
    }
  }

  const onSupervisorCheck = ({ supervisorBadge }: FormData) => {
    clearErrors('supervisorBadge')

    dispatch(actions.receiveValidateSupervisorBadge({
      badge: supervisorBadge,
      onError: (error: string) => {
        setError('supervisorBadge', { message: error })
      },
      onSuccess: () => {
        nextItemCountMode()
      },
    }))
  }

  const onSupervisorCount = ({ supervisorCount }: FormData) => {
    const quantity = Number(supervisorCount)

    if (quantity !== totalItemQuantity) {
      setDivergentQuantity(quantity)
      setShowModalDivergentQuantityConfirmation(true)
    } else {
      dispatch(actions.setProperty({ property: 'receiveSupervisor', value: undefined }))
      setPageMode(ReceiveItemPageModes.ITEM_MOVEMENT)
    }
  }

  const onSupervisorBatchesCountSuccess = () => {
    dispatch(actions.setProperty({ property: 'receiveSupervisor', value: undefined }))
    setPageMode(ReceiveItemPageModes.ITEM_MOVEMENT)
  }

  return (
    <>
      <S.Container>
        <S.ContainerItem>
          <S.ContainerBox style={{ marginTop: '.6rem' }}>
            <TextLabel width='100%' height='3.5rem' backgroundColor='#7feb77'>
              <Typography fontSize='14px' fontWeight='bold'>{item.productDescription}</Typography>
            </TextLabel>
          </S.ContainerBox>
          {itemCountMode === ItemCountModes.OPERATOR_COUNT && (
          <S.Form onSubmit={handleSubmit(onOperatorCount)}>
            <Typography fontSize='15px' fontWeight='bold'>PRIMEIRA CONTAGEM (OPERADOR)</Typography>
            <S.CustomFieldMui
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ cursor: 'pointer' }}
                    data-testid='input-adornment-test-id'
                  >
                    UN
                  </InputAdornment>
                ),
                inputProps: {
                  ...register('operatorCount', {
                    required: 'Campo obrigatório',
                    min: { value: 0, message: 'Deve ser zero ou um valor positivo' },
                    pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser inteiro' },
                  }),
                  'data-testid': 'input-operator-count-test-id',
                },
              }}
              error={!!errors.operatorCount}
              helperText={errors.operatorCount?.message}
              label='Contagem do produto'
              name='operator-count'
              testId='input-operator-count-test-id'
              type='number'
              size='small'
              fullWidth
              autoComplete='off'
              autoFocus
            />
          </S.Form>
          )}
          {itemCountMode === ItemCountModes.SUPERVISOR_BADGE && (
          <S.Form onSubmit={handleSubmit(onSupervisorCheck)}>
            <Typography fontSize='15px' fontWeight='bold'>CHAME O FISCAL!</Typography>
            <S.CustomFieldMui
              inputProps={{
                ...register('supervisorBadge', { required: 'Campo obrigatório' }),
                'data-testid': 'input-supervisor-badge-test-id',
              }}
              error={!!errors.supervisorBadge}
              helperText={errors.supervisorBadge?.message}
              label='Crachá do fiscal'
              name='supervisor-badge'
              testId='input-supervisor-badge-test-id'
              type='password'
              size='small'
              fullWidth
              autoComplete='off'
              autoFocus
            />
          </S.Form>
          )}
          {itemCountMode === ItemCountModes.SUPERVISOR_COUNT && receiveSupervisor && (
          <S.Form onSubmit={handleSubmit(onSupervisorCount)}>
            <Typography fontSize='15px' fontWeight='bold'>SEGUNDA CONTAGEM (FISCAL)</Typography>
            <Typography fontSize='15px' fontWeight='bold'>{receiveSupervisor.name}</Typography>
            <S.CustomFieldMui
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ cursor: 'pointer' }}
                    data-testid='input-adornment-test-id'
                  >
                    UN
                  </InputAdornment>
                ),
                inputProps: {
                  ...register('supervisorCount', {
                    required: 'Campo obrigatório',
                    min: { value: 0, message: 'Deve ser zero ou um valor positivo' },
                    pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser inteiro' },
                  }),
                  'data-testid': 'input-supervisor-count-test-id',
                },
              }}
              error={!!errors.supervisorCount}
              helperText={errors.supervisorCount?.message}
              label='Contagem do produto'
              name='supervisor-count'
              testId='input-supervisor-count-test-id'
              type='number'
              size='small'
              fullWidth
              autoComplete='off'
              autoFocus
            />
          </S.Form>
          )}
          {itemCountMode === ItemCountModes.SUPERVISOR_BATCHES_COUNT && receiveSupervisor && (
            <>
              <Typography fontSize='15px' fontWeight='bold'>CONTAGEM DE LOTES (FISCAL)</Typography>
              <Typography fontSize='15px' fontWeight='bold'>{receiveSupervisor.name}</Typography>
              <FormCountBatches
                idCage={idCage}
                idModel={item.idModel}
                idLocation={item.bestPosition.id}
                productDescription={item.productDescription}
                supervisorBadge={receiveSupervisor.badge}
                nextStep={onSupervisorBatchesCountSuccess}
              />
            </>
          )}
        </S.ContainerItem>
      </S.Container>
      <ConfirmOrRejectModal
        show={showModalDivergentQuantityConfirmation}
        title='Confirmação de quantidade divergente'
        dataTestId='modal-divergent-quantity-confirmation-test-id'
        customizableDescription={(
          <S.ConfirmModalContainer>
            <S.ConfirmModalText>
              Ao confirmar a ação, você afirma que a contagem realizada do produto <strong>{item.productDescription}</strong> é de <strong>{divergentQuantity} unidades</strong>.
            </S.ConfirmModalText>
            <S.ConfirmModalConfirmationText>
              Deseja prosseguir?
            </S.ConfirmModalConfirmationText>
          </S.ConfirmModalContainer>
        )}
        onClose={onCloseModalDivergentQuantityConfirmation}
        onReject={onCloseModalDivergentQuantityConfirmation}
        onConfirm={divergentQuantityConfirmation}
      />
    </>
  )
}

export default ReceiveItemCount
